import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getSettings, updateSetting } from '../api';
import { Settings as SettingsIcon, Info } from 'lucide-react';

export default function Settings() {
  const { isAdmin } = useAuth();
  const [consultationPrice, setConsultationPrice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const response = await getSettings();
        const list = response.data?.data || [];
        const priceSetting = list.find((s) => s.key === 'consultation_price');
        if (priceSetting) {
          const numeric = typeof priceSetting.value_numeric === 'number'
            ? priceSetting.value_numeric
            : parseFloat(priceSetting.value);
          if (!isNaN(numeric)) {
            setConsultationPrice(String(numeric));
          }
        }
      } catch (error) {
        toast.error('Error al cargar configuración');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const value = consultationPrice.trim();
    if (value === '') {
      toast.error('El precio de consulta es obligatorio');
      return;
    }
    const numeric = parseFloat(value);
    if (isNaN(numeric) || numeric < 0) {
      toast.error('El precio debe ser un número mayor o igual a 0');
      return;
    }

    try {
      setIsSaving(true);
      await updateSetting('consultation_price', numeric);
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 md:p-10">
        <div className="text-center py-12">
          <SettingsIcon className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#2B2D42]">Acceso restringido</h3>
          <p className="text-[#5D5C61] mt-1">
            Solo los administradores pueden acceder a la configuración
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Configuración</h1>
        <p className="text-[#5D5C61] mt-1">Parámetros del sistema</p>
      </div>

      <div className="max-w-2xl space-y-6">
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-xl font-['Manrope'] flex items-center gap-2">
              <Info className="w-5 h-5 text-[#335C67]" />
              Configuración simplificada
            </CardTitle>
            <CardDescription>
              Las opciones de duración de citas, intervalos SOAP y recordatorios han sido
              desactivadas. El horario de citas permitido es de lunes a sábado de 12:00 a 20:00.
              Para citas fuera de ese horario se puede usar la opción &quot;Forzar Cita&quot; al agendar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#5D5C61]">
              Notas SOAP: la numeración es por paciente dentro de cada expediente.
              Asistencia y pago: se gestionan desde el detalle de cada cita.
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">
              Precio de consulta
            </CardTitle>
            <CardDescription>
              Este valor podrá usarse en futuras funciones de cobro, cotización o visualización en otras pantallas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="consultation_price">Precio de consulta</Label>
                <div className="flex items-center gap-2">
                  <span className="text-[#5D5C61]">$</span>
                  <Input
                    id="consultation_price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ej. 800"
                    value={consultationPrice}
                    onChange={(e) => setConsultationPrice(e.target.value)}
                    disabled={isLoading || isSaving}
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                disabled={isLoading || isSaving}
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
